import { Box, Image, Paragraph } from '@zillow/constellation-10';
import { Source, withPreload } from '@zillow/zrs-cms';
import React from 'react';

import { Post } from '../../types/shared';

interface PostFeaturedImageProps {
    post: Post;
}

const ImagePreload = withPreload(Image);
const SourcePreload = withPreload(Source);

const PostFeaturedImage: React.FC<PostFeaturedImageProps> = ({ post }) => {
    const mediumImage = post.featuredImage?.mediaDetails?.sizes?.medium;
    const largeImage = post.featuredImage?.mediaDetails?.sizes?.large;
    const fullImage = post.featuredImage
        ? {
              sourceUrl: post.featuredImage.sourceUrl,
              width: post.featuredImage.mediaDetails.width,
              height: post.featuredImage.mediaDetails.height,
          }
        : null;

    if (!post.featuredImage || (!mediumImage && !largeImage && !fullImage)) {
        return null;
    }

    return (
        <>
            <Box css={{ textAlign: 'center' }}>
                <Box
                    asChild
                    css={{
                        '& img, & source': {
                            borderRadius: '600',
                            overflow: 'hidden',
                            marginBottom: 'tighter',
                            aspectRatio: '16 / 9',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            height: 'auto',
                            width: '100%',
                        },
                    }}
                >
                    <picture>
                        {!!mediumImage && (
                            <SourcePreload
                                srcSet={mediumImage?.sourceUrl}
                                media="md_lte"
                                width={mediumImage.width}
                                height={mediumImage.height}
                            />
                        )}
                        {!!largeImage && (
                            <ImagePreload
                                src={
                                    largeImage?.sourceUrl ||
                                    mediumImage?.sourceUrl
                                }
                                alt={
                                    post.featuredImage.altText ||
                                    post.featuredImage.caption?.raw ||
                                    post.title?.raw
                                }
                                width={largeImage.width}
                                height={largeImage.height}
                            />
                        )}
                        {!largeImage && !!fullImage && (
                            <ImagePreload
                                src={fullImage.sourceUrl}
                                width={fullImage.width}
                                height={fullImage.height}
                                alt={
                                    post.featuredImage.altText ||
                                    post.featuredImage.caption?.raw ||
                                    post.title?.raw
                                }
                            />
                        )}
                    </picture>
                </Box>
            </Box>
            {!!post.featuredImage?.caption?.raw && (
                <Box css={{ xxl: { textAlign: 'center' } }}>
                    {/* @ts-expect-error - Paragraph requires children but we're using dangerouslySetInnerHTML */}
                    <Paragraph
                        css={{ color: 'text.subtle' }}
                        textStyle="body-sm"
                        dangerouslySetInnerHTML={{
                            __html: post.featuredImage.caption.raw,
                        }}
                    />
                </Box>
            )}
        </>
    );
};

export default PostFeaturedImage;
